import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaStore, FaBoxes, FaShoppingCart, 
  FaBarcode, FaTags, FaBoxOpen, FaTag, FaInfoCircle,
  FaExclamationTriangle, FaCheck, FaGlobe, FaCertificate,
  FaShieldAlt, FaRulerCombined, FaWeightHanging, FaBox,
  FaShippingFast, FaHashtag, FaSearch, FaCalendarCheck,
  FaSortNumericUpAlt, FaCubes, FaCalendarPlus, FaCalendarAlt
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './ProductDetails.css';

// Define dummy products for "experience" role - enriched for details view
const dummyExperienceProducts = [
  { 
    id: 'exp001', // Matches ID from ProductList dummy data
    title: 'Experience T-Shirt - Galaxy', 
    images: [
      { id:'img1', url: '/tshirtgalaxy.png', alt_text: 'Galaxy Tee Front' },
      { id:'img2', url: '/tshirtgalaxy.png', alt_text: 'Galaxy Tee Back' },
      { id:'img3', url: '/tshirtgalaxy.png', alt_text: 'Galaxy Tee Detail' }
    ],
    status: 'active', 
    vendor: 'Experience Wear', 
    product_type: 'Apparel',
    category: 'Tops',
    body_html: '<p>Explore the cosmos with our <strong>Galaxy T-Shirt</strong>! Made from 100% premium ringspun cotton for ultimate comfort. Features a vibrant, high-quality print that won\'t fade.</p><ul><li>Soft and breathable fabric</li><li>Unisex fit</li><li>Durable print</li></ul>',
    tags: ['cotton', 'new', 'space', 'galaxy', 'apparel'],
    options: [{ name: 'Size', values: ['S', 'M', 'L', 'XL'] }, { name: 'Color', values: ['Cosmic Black', 'Star White'] }],
    variants: [
      { id: 'v_exp001_1', title: 'S / Cosmic Black', price: '29.99', compare_at_price: '35.00', inventory_quantity: 15, sku: 'EXP-TS-GAL-S-BLK', barcode: '1234567890123', taxable: true, requires_shipping: true, material: 'Cotton', inventory_policy: 'deny', inventory_management: 'shopify', weight: '0.2', weight_unit: 'kg', dimensions: { length: '30', width: '20', height: '2', unit: 'cm'} },
      { id: 'v_exp001_2', title: 'M / Cosmic Black', price: '29.99', compare_at_price: '35.00', inventory_quantity: 5, sku: 'EXP-TS-GAL-M-BLK', barcode: '1234567890124', taxable: true, requires_shipping: true, material: 'Cotton', inventory_policy: 'deny', inventory_management: 'shopify', weight: '0.2', weight_unit: 'kg' },
      { id: 'v_exp001_3', title: 'L / Star White', price: '29.99', inventory_quantity: 8, sku: 'EXP-TS-GAL-L-WHT', barcode: '1234567890125', taxable: true, requires_shipping: true, material: 'Cotton', inventory_policy: 'deny', inventory_management: 'shopify', weight: '0.2', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com', // To trigger isAdminView sections
    country_of_origin: 'Portugal',
    seo_title: 'Galaxy Print Experience T-Shirt | Cosmic Apparel',
    seo_description: 'Get your hands on the unique Galaxy Experience T-Shirt. Premium quality, vibrant space print.',
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 days ago
    updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    materials: ['100% Ringspun Cotton', 'Eco-friendly ink'],
    certifications: ['OEKO-TEX Standard 100'],
    warranty: '1 Year on Print Quality',
    hs_code: '6109.10',
    package_info: { weight: '0.25', weight_unit: 'kg', dimensions: { length: '35', width: '25', height: '3', unit: 'cm' }, items_per_package: 1 },
    minimum_order_quantity: 1,
    lead_time: '2-3 business days'
  },
  { 
    id: 'exp002', // Matches ID from ProductList dummy data
    title: 'Experience Mug - Astronaut', 
    images: [{ id:'img_mug1', url: '/astromug.png', alt_text: 'Astronaut Mug' }],
    status: 'active', 
    vendor: 'Experience Goods', 
    product_type: 'Homeware',
    category: 'Drinkware',
    body_html: '<p>Start your day with a cosmic coffee! This <strong>Astronaut Mug</strong> is made of durable ceramic and is dishwasher safe.</p>',
    tags: ['ceramic', 'gift', 'space', 'astronaut', 'coffee'],
    options: [{ name: 'Style', values: ['Standard', 'Large'] }],
    variants: [
      { id: 'v_exp002_1', title: 'Standard', price: '15.99', inventory_quantity: 30, sku: 'EXP-MUG-AST-STD', barcode: '0987654321098', taxable: true, requires_shipping: true, material: 'Ceramic', weight: '0.4', weight_unit: 'kg' },
      { id: 'v_exp002_2', title: 'Large', price: '18.99', inventory_quantity: 0, sku: 'EXP-MUG-AST-LRG', barcode: '0987654321099', taxable: true, requires_shipping: true, material: 'Ceramic', weight: '0.5', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com', // To trigger isAdminView sections
    country_of_origin: 'China',
    created_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    warranty: '6 Months on Defects',
    hs_code: '6912.00',
  },
  { 
    id: 'exp003', 
    title: 'Experience Hoodie - Nebula', 
    images: [{ id:'img_hoodie1', url: '/hoodie.png', alt_text: 'Nebula Hoodie' }],
    status: 'draft', 
    vendor: 'Experience Wear', 
    product_type: 'Apparel',
    category: 'Outerwear',
    body_html: '<p>Stay warm and stylish with our <strong>Nebula Hoodie</strong>. Features a stunning all-over nebula print.</p>',
    tags: ['warm', 'winter', 'hoodie', 'nebula', 'stars'],
    options: [{ name: 'Size', values: ['S', 'M', 'L'] }, { name: 'Color', values: ['Cosmic Blue', 'Deep Purple'] }],
    variants: [
      { id: 'v_exp003_1', title: 'S / Cosmic Blue', price: '49.99', inventory_quantity: 8, sku: 'EXP-HOOD-NEB-S-BLU', barcode: '2345678901234', taxable: true, requires_shipping: true, material: 'Cotton Blend', weight: '0.5', weight_unit: 'kg' },
      { id: 'v_exp003_2', title: 'M / Cosmic Blue', price: '49.99', inventory_quantity: 12, sku: 'EXP-HOOD-NEB-M-BLU', barcode: '2345678901235', taxable: true, requires_shipping: true, material: 'Cotton Blend', weight: '0.5', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'Vietnam',
    created_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    materials: ['80% Cotton, 20% Polyester'],
    hs_code: '6110.20'
  },
  { 
    id: 'exp004', 
    title: 'Experience Cap - Rocket', 
    images: [{ id:'img_cap1', url: '/rocketcap.png', alt_text: 'Rocket Cap' }],
    status: 'archived', 
    vendor: 'Experience Gear', 
    product_type: 'Accessory',
    category: 'Headwear',
    body_html: '<p>Blast off in style with the <strong>Rocket Cap</strong>. Embroidered design, adjustable strap.</p>',
    tags: ['summer', 'promo', 'cap', 'rocket', 'embroidered'],
    options: [{ name: 'Style', values: ['Standard'] }],
    variants: [
      { id: 'v_exp004_1', title: 'One Size', price: '19.99', inventory_quantity: 0, sku: 'EXP-CAP-RCK-OS', barcode: '3456789012345', taxable: true, requires_shipping: true, material: 'Canvas', weight: '0.1', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'Bangladesh',
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
  { 
    id: 'exp005', 
    title: 'Experience Water Bottle - Stainless Steel', 
    images: [{ id:'img_bottle1', url: '/waterbottle.png', alt_text: 'Steel Water Bottle' }],
    status: 'active', 
    vendor: 'Experience Goods', 
    product_type: 'Drinkware',
    category: 'Accessories',
    body_html: '<p>Stay hydrated with our sleek <strong>Stainless Steel Water Bottle</strong>. Eco-friendly and durable.</p>',
    tags: ['eco', 'reusable', 'steel', 'water bottle', 'hydration'],
    options: [{ name: 'Capacity', values: ['500ml', '750ml'] }],
    variants: [
      { id: 'v_exp005_1', title: '500ml', price: '24.99', inventory_quantity: 30, sku: 'EXP-BOT-STL-500', barcode: '4567890123456', taxable: true, requires_shipping: true, material: 'Stainless Steel', weight: '0.3', weight_unit: 'kg' },
      { id: 'v_exp005_2', title: '750ml', price: '27.99', inventory_quantity: 50, sku: 'EXP-BOT-STL-750', barcode: '4567890123457', taxable: true, requires_shipping: true, material: 'Stainless Steel', weight: '0.4', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'China',
    materials: ['Food-grade Stainless Steel'],
    certifications: ['FDA Approved'],
    warranty: '1 Year',
    package_info: { weight: '0.45', weight_unit: 'kg', dimensions: { length: '25', width: '8', height: '8', unit: 'cm' }, items_per_package: 1 }
  },
  { 
    id: 'exp006', 
    title: 'Experience Socks - Alien Pattern', 
    images: [{ id:'img_socks1', url: '/alienpattern.png', alt_text: 'Alien Pattern Socks' }],
    status: 'active', 
    vendor: 'Experience Wear', 
    product_type: 'Apparel',
    category: 'Accessories',
    body_html: '<p>Funky <strong>Alien Pattern Socks</strong> to show off your unique style. Comfortable and quirky.</p>',
    tags: ['fun', 'novelty', 'socks', 'alien', 'cotton'],
    options: [{ name: 'Size', values: ['One Size'] }],
    variants: [
      { id: 'v_exp006_1', title: 'One Size', price: '12.99', inventory_quantity: 22, sku: 'EXP-SOCK-ALN-OS', barcode: '5678901234567', taxable: true, requires_shipping: true, material: 'Cotton Blend', weight: '0.05', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    materials: ['70% Cotton, 25% Polyester, 5% Spandex'],
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
  { 
    id: 'exp007', 
    title: 'Experience Backpack - Voyager', 
    images: [{ id:'img_pack1', url: '/voyagerbag.png', alt_text: 'Voyager Backpack' }],
    status: 'active', 
    vendor: 'Experience Gear', 
    product_type: 'Accessory',
    category: 'Bags',
    body_html: '<p>The <strong>Voyager Backpack</strong> is your perfect companion for daily commutes or weekend adventures. Durable and spacious.</p>',
    tags: ['travel', 'durable', 'backpack', 'commute', 'gear'],
    options: [{ name: 'Color', values: ['Stealth Black', 'Olive Green'] }],
    variants: [
      { id: 'v_exp007_1', title: 'Stealth Black', price: '59.99', inventory_quantity: 18, sku: 'EXP-PACK-VOY-BLK', barcode: '6789012345678', taxable: true, requires_shipping: true, material: 'Nylon', weight: '0.8', weight_unit: 'kg' },
      { id: 'v_exp007_2', title: 'Olive Green', price: '59.99', inventory_quantity: 10, sku: 'EXP-PACK-VOY-GRN', barcode: '6789012345679', taxable: true, requires_shipping: true, material: 'Nylon', weight: '0.8', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'Indonesia',
    warranty: '2 Years',
    materials: ['Water-resistant Nylon', 'YKK Zippers'],
    package_info: { weight: '0.9', weight_unit: 'kg', dimensions: { length: '45', width: '30', height: '15', unit: 'cm' }, items_per_package: 1 }
  },
  // Added details for exp008 to exp016
  {
    id: 'exp008',
    title: 'Experience Poster - Solar System',
    images: [{ id: 'img_poster1', url: '/poster.png', alt_text: 'Solar System Poster' }],
    status: 'draft',
    vendor: 'Experience Prints',
    product_type: 'Decor',
    category: 'Wall Art',
    body_html: '<p>Decorate your space with this educational and beautiful <strong>Solar System Poster</strong>. High-quality print on thick paper.</p>',
    tags: ['education', 'wall art', 'space', 'solar system', 'decor'],
    options: [{ name: 'Size', values: ['A3', 'A2'] }],
    variants: [
      { id: 'v_exp008_1', title: 'A3', price: '19.99', inventory_quantity: 40, sku: 'EXP-POST-SOL-A3', barcode: '7890123456789', taxable: true, requires_shipping: true, material: 'Paper 250gsm', weight: '0.1', weight_unit: 'kg' },
      { id: 'v_exp008_2', title: 'A2', price: '29.99', inventory_quantity: 20, sku: 'EXP-POST-SOL-A2', barcode: '7890123456780', taxable: true, requires_shipping: true, material: 'Paper 250gsm', weight: '0.2', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'Germany',
    created_at: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'exp009',
    title: 'Experience Keychain - Spaceship',
    images: [{ id: 'img_keychain1', url: '/keychain.png', alt_text: 'Spaceship Keychain' }],
    status: 'active',
    vendor: 'Experience Goods',
    product_type: 'Accessory',
    category: 'Gifts',
    body_html: '<p>Carry a piece of the cosmos with you! This <strong>Spaceship Keychain</strong> is made from durable metal.</p>',
    tags: ['collectible', 'keychain', 'spaceship', 'gift', 'accessory'],
    options: [{ name: 'Material', values: ['Metal Alloy'] }],
    variants: [
      { id: 'v_exp009_1', title: 'Standard', price: '9.99', inventory_quantity: 100, sku: 'EXP-KEY-SPC-STD', barcode: '8901234567890', taxable: true, requires_shipping: true, material: 'Metal Alloy', weight: '0.03', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'China',
    warranty: '3 Months',
    created_at: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'exp010',
    title: 'Experience Phone Case - Mars Rover',
    images: [{ id: 'img_case1', url: '/marsrover.png', alt_text: 'Mars Rover Phone Case' }],
    status: 'active',
    vendor: 'Experience Tech',
    product_type: 'Accessory',
    category: 'Tech Accessories',
    body_html: '<p>Protect your phone with an out-of-this-world <strong>Mars Rover Phone Case</strong>. Durable and stylish.</p>',
    tags: ['protective', 'mars', 'phone case', 'tech', 'rover'],
    options: [{ name: 'Model', values: ['iPhone X', 'iPhone 12', 'Samsung S21'] }],
    variants: [
      { id: 'v_exp010_1', title: 'iPhone X', price: '22.50', inventory_quantity: 3, sku: 'EXP-CASE-MAR-IPX', barcode: '9012345678901', taxable: true, requires_shipping: true, material: 'TPU', weight: '0.05', weight_unit: 'kg' },
      { id: 'v_exp010_2', title: 'iPhone 12', price: '22.50', inventory_quantity: 7, sku: 'EXP-CASE-MAR-IP12', barcode: '9012345678902', taxable: true, requires_shipping: true, material: 'TPU', weight: '0.05', weight_unit: 'kg' },
      { id: 'v_exp010_3', title: 'Samsung S21', price: '22.50', inventory_quantity: 5, sku: 'EXP-CASE-MAR-S21', barcode: '9012345678903', taxable: true, requires_shipping: true, material: 'TPU', weight: '0.05', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'Korea',
    materials: ['Shock-absorbent TPU', 'Polycarbonate back'],
    warranty: '6 Months',
    created_at: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'exp011',
    title: 'Experience Beanie - Comet Trail',
    images: [], // No image
    status: 'archived',
    vendor: 'Experience Wear',
    product_type: 'Apparel',
    category: 'Headwear',
    body_html: '<p>Keep warm with the <strong>Comet Trail Beanie</strong>. Soft knit for comfort. Limited edition.</p>',
    tags: ['winter', 'limited', 'beanie', 'comet', 'knit'],
    options: [{ name: 'Size', values: ['One Size'] }],
    variants: [
      { id: 'v_exp011_1', title: 'One Size', price: '18.00', inventory_quantity: 0, sku: 'EXP-BEAN-COM-OS', barcode: '0123456789012', taxable: true, requires_shipping: true, material: 'Acrylic Knit', weight: '0.08', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'Turkey',
    created_at: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'exp012',
    title: 'Experience Stickers Pack - Cosmic',
    images: [{ id: 'img_stickers1', url: '/stickers.png', alt_text: 'Cosmic Stickers Pack' }],
    status: 'active',
    vendor: 'Experience Prints',
    product_type: 'Stationery',
    category: 'Accessories',
    body_html: '<p>Decorate your belongings with this <strong>Cosmic Stickers Pack</strong>. Includes 50 unique designs.</p>',
    tags: ['decor', 'laptop', 'stickers', 'cosmic', 'stationery'],
    options: [{ name: 'Pack Size', values: ['Pack of 50'] }],
    variants: [
      { id: 'v_exp012_1', title: 'Pack of 50', price: '10.00', inventory_quantity: 200, sku: 'EXP-STK-COS-P50', barcode: '1234509876543', taxable: true, requires_shipping: true, material: 'Vinyl', weight: '0.04', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'USA',
    materials: ['Durable Vinyl', 'Waterproof'],
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'exp013',
    title: 'Experience Scarf - Starry Night',
    images: [{ id: 'img_scarf1', url: '/scarf.png', alt_text: 'Starry Night Scarf' }],
    status: 'active',
    vendor: 'Experience Wear',
    product_type: 'Apparel',
    category: 'Accessories',
    body_html: '<p>Wrap yourself in the beauty of a <strong>Starry Night Scarf</strong>. Soft and elegant, perfect for any occasion.</p>',
    tags: ['fashion', 'soft', 'scarf', 'stars', 'nightsky'],
    options: [{ name: 'Style', values: ['Standard'] }],
    variants: [
      { id: 'v_exp013_1', title: 'Standard', price: '25.00', inventory_quantity: 12, sku: 'EXP-SCRF-STR-STD', barcode: '2345098765432', taxable: true, requires_shipping: true, material: 'Viscose Blend', weight: '0.15', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'India',
    materials: ['70% Viscose, 30% Silk'],
    created_at: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'exp014',
    title: 'Experience Notebook - Constellation',
    images: [{ id: 'img_notebook1', url: '/notebook.png', alt_text: 'Constellation Notebook' }],
    status: 'draft',
    vendor: 'Experience Prints',
    product_type: 'Stationery',
    category: 'Office Supplies',
    body_html: '<p>Jot down your thoughts in this beautiful <strong>Constellation Notebook</strong>. A5 size, lined pages.</p>',
    tags: ['writing', 'journal', 'notebook', 'constellation', 'stationery'],
    options: [{ name: 'Format', values: ['A5 Lined', 'A5 Blank'] }],
    variants: [
      { id: 'v_exp014_1', title: 'A5 Lined', price: '14.50', inventory_quantity: 60, sku: 'EXP-NOTE-CON-A5L', barcode: '3450987654321', taxable: true, requires_shipping: true, material: 'Recycled Paper', weight: '0.25', weight_unit: 'kg' },
      { id: 'v_exp014_2', title: 'A5 Blank', price: '14.50', inventory_quantity: 30, sku: 'EXP-NOTE-CON-A5B', barcode: '3450987654320', taxable: true, requires_shipping: true, material: 'Recycled Paper', weight: '0.25', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'Italy',
    materials: ['Cover: Hardbound Cardstock', 'Paper: 80gsm Recycled Lined/Blank'],
    package_info: { weight: '0.3', weight_unit: 'kg', dimensions: { length: '22', width: '16', height: '2', unit: 'cm' }, items_per_package: 1 },
    created_at: new Date(Date.now() - 22 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'exp015',
    title: 'Experience Desk Mat - Deep Space',
    images: [{ id: 'img_deskmat1', url: '/deskmat.png', alt_text: 'Deep Space Desk Mat' }],
    status: 'active',
    vendor: 'Experience Goods',
    product_type: 'Office',
    category: 'Desk Accessories',
    body_html: '<p>Enhance your workspace with the <strong>Deep Space Desk Mat</strong>. Large size for comfort and style.</p>',
    tags: ['gaming', 'ergonomic', 'desk mat', 'space', 'office'],
    options: [{ name: 'Size', values: ['Large', 'Extra Large'] }],
    variants: [
      { id: 'v_exp015_1', title: 'Large', price: '30.00', inventory_quantity: 25, sku: 'EXP-MAT-DSP-L', barcode: '4509876543210', taxable: true, requires_shipping: true, material: 'Neoprene, Rubber Base', weight: '0.7', weight_unit: 'kg' },
      { id: 'v_exp015_2', title: 'Extra Large', price: '38.00', inventory_quantity: 15, sku: 'EXP-MAT-DSP-XL', barcode: '4509876543211', taxable: true, requires_shipping: true, material: 'Neoprene, Rubber Base', weight: '0.9', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'China',
    materials: ['Surface: Smooth Cloth', 'Base: Anti-slip Rubber'],
    dimensions: { length: '90', width: '40', height: '0.3', unit: 'cm' }, // For Large size
    created_at: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'exp016',
    title: 'Experience Puzzle - Earth View',
    images: [{ id: 'img_puzzle1', url: '/puzzle.png', alt_text: 'Earth View Puzzle' }],
    status: 'active',
    vendor: 'Experience Games',
    product_type: 'Toy',
    category: 'Games & Puzzles',
    body_html: '<p>Challenge yourself with the stunning <strong>Earth View Puzzle</strong>. 1000 pieces of detailed imagery.</p>',
    tags: ['family', 'challenge', 'puzzle', 'earth', '1000pc'],
    options: [{ name: 'Pieces', values: ['1000 pieces'] }],
    variants: [
      { id: 'v_exp016_1', title: '1000 pieces', price: '28.00', inventory_quantity: 10, sku: 'EXP-PUZ-EAR-1K', barcode: '5098765432109', taxable: true, requires_shipping: true, material: 'Recycled Cardboard', weight: '0.6', weight_unit: 'kg' },
    ],
    user_email: 'experience@example.com',
    country_of_origin: 'Poland',
    materials: ['High-quality Recycled Cardboard', 'Non-toxic Inks'],
    certifications: ['CE Certified'],
    package_info: { weight: '0.7', weight_unit: 'kg', dimensions: { length: '35', width: '25', height: '5', unit: 'cm' }, items_per_package: 1 },
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  }
];

const ProductDetails = () => {
  const { t } = useTranslation();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const { productId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProductDetails = async () => {
      setIsLoading(true); // Ensure loading is true at the start
      setError(null); // Reset error
      try {
        const token = localStorage.getItem('access_token');
        const userDataString = localStorage.getItem('userData');
        let userRole = null;
        let isAdmin = false;

        if (!token) {
          navigate('/'); // Redirect if no token
          setIsLoading(false);
          return;
        }

        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            userRole = userData.role;
            isAdmin = userRole === 'admin';
          } catch (parseError) {
            console.error("Failed to parse user data from localStorage:", parseError);
            // Potentially set error or proceed as non-admin/non-experience user
          }
        }

        if (userRole === 'experience') {
          console.log(`Experience user detected. Looking for dummy product with ID: ${productId}`);
          const dummyProduct = dummyExperienceProducts.find(p => p.id === productId);
          if (dummyProduct) {
            // Simulate image 'url' if it's 'src' in dummy data, to match expected structure
            const productData = {
              ...dummyProduct,
              images: dummyProduct.images?.map(img => ({ ...img, url: img.src || img.url })) || []
            };
            setProduct(productData);
          } else {
            console.warn(`Dummy product with ID ${productId} not found for experience user.`);
            setError(t('products.detailsNotFoundExperience', 'This experience product detail is not available.'));
            setProduct(null);
          }
          setIsLoading(false);
          return; // Stop further execution for experience user
        }

        // Existing logic for admin and other users
        let endpoint;
        if (isAdmin) {
          endpoint = `/api/shopify/get_local_product/${productId}`;
           console.log("Admin user detected, fetching details from local endpoint:", endpoint);
        } else {
          endpoint = `/api/shopify/get_product/${productId}`;
          console.log("Non-admin/non-experience user or role unknown, fetching details from Shopify endpoint:", endpoint);
        }

        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data) {
          // Ensure images have a 'url' property, mapping from 'src' if necessary (common from Shopify)
          const productData = {
            ...response.data,
            images: response.data.images?.map(img => ({ ...img, url: img.src || img.url })) || []
          };
          setProduct(productData);
        } else {
          console.warn("Product details data not found in expected format:", response.data);
          setProduct(null);
          setError(t('products.detailsNotFound'));
        }

      } catch (err) {
        console.error("Error fetching product details:", err);
        setError(err.message);
         if (err.response && (err.response.status === 401 || err.response.status === 422)) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('userData');
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId, t, navigate]); // Added navigate to dependency array

  const getStockStatus = (inventory) => {
    if (inventory <= 0) return 'out-of-stock';
    if (inventory < 10) return 'low-stock';
    return 'in-stock';
  };

  // Helper function to check if the user is admin based on product data structure
  // (Assumes certain fields ONLY exist for admin/local products OR for enriched experience products)
  const isAdminView = () => {
      // For experience user, this will be true if 'user_email' is in their dummy product data
      return product && product.hasOwnProperty('user_email'); 
  };

  // Helper to format dimensions
  const formatDimensions = (dims) => {
      if (!dims || !dims.length || !dims.width || !dims.height || !dims.unit) return '-';
      return `${dims.length}x${dims.width}x${dims.height} ${dims.unit}`;
  }

  // Helper to format dates (optional, adjust as needed)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      // Handle both ISO string and MongoDB $date object format if necessary
      const date = new Date(typeof dateString === 'object' && dateString.$date ? dateString.$date : dateString);
      return date.toLocaleString(); // Or use a library like date-fns for better formatting
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString; // Return original string if formatting fails
    }
  };

  if (error) return (
    <div className="una-product-error">
      <FaExclamationTriangle />
      <h2>{t('common.error')}</h2>
      <p>{error}</p>
      <button className="una-back-button" onClick={() => navigate('/products')}>
        <FaArrowLeft /> {t('common.backToProducts')}
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="una-dashboard-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>{t('products.loadingDetails')}</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
          <div className="una-loading-metrics">
            <div className="una-metric-item">
              <FaStore />
              <span>{t('products.fetchingProduct')}</span>
            </div>
            <div className="una-metric-item">
              <FaBoxOpen />
              <span>{t('products.processingInventory')}</span>
            </div>
            <div className="una-metric-item">
              <FaBarcode />
              <span>{t('products.loadingVariants')}</span>
            </div>
            <div className="una-metric-item">
              <FaTags />
              <span>{t('products.processingTags')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Add a check for product === null after loading, before trying to render details
  if (!product) {
    return (
      <div className="una-product-error">
        <FaExclamationTriangle />
        <h2>{t('products.productUnavailableTitle', 'Product Not Available')}</h2>
        <p>{error || t('products.detailsNotFound')}</p>
        <button className="una-back-button" onClick={() => navigate('/products')}>
          <FaArrowLeft /> {t('common.backToProducts')}
        </button>
      </div>
    );
  }

  return (
    <div className="una-product-details-container">
      <button className="una-back-button" onClick={() => navigate('/products')}>
        <FaArrowLeft /> {t('common.backToProducts')}
      </button>
      
      <div className="una-product-hero">
        <div className="una-product-hero-media">
          <div className="una-product-image-container">
            {product.images && product.images.length > 0 ? (
              <img 
                src={product.images[activeImageIndex].url}
                alt={product.images[activeImageIndex].alt_text || product.title}
                className="una-product-image"
              />
            ) : (
              <div className="una-product-no-image">
                <FaBoxOpen />
                <span>{t('products.noImage')}</span>
              </div>
            )}
          </div>
          
          {product.images && product.images.length > 1 && (
            <div className="una-product-thumbnails">
              {product.images.map((image, index) => (
                <div 
                  key={image.id || index}
                  className={`una-thumbnail ${activeImageIndex === index ? 'active' : ''}`}
                  onClick={() => setActiveImageIndex(index)}
                >
                  <img src={image.url} alt={image.alt_text || `${product.title} ${index + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="una-product-hero-info">
          <div className="una-product-headline">
            <h1 className="una-product-title">{product.title}</h1>
            <div className="una-product-status-badge">{product.status}</div>
          </div>
          
          <div className="una-product-meta">
            <div className="una-meta-item">
              <span className="una-meta-label">{t('products.vendor')}</span>
              <span className="una-meta-value">{product.vendor || '-'}</span>
            </div>
            <div className="una-meta-item">
              <span className="una-meta-label">{t('products.type')}</span>
              <span className="una-meta-value">{product.product_type || '-'}</span>
            </div>
            {product.category && (
               <div className="una-meta-item">
                 <span className="una-meta-label"><FaCubes /> {t('products.category')}</span>
                 <span className="una-meta-value">{product.category}</span>
               </div>
            )}
            <div className="una-meta-item">
              <span className="una-meta-label">{t('products.totalInventory')}</span>
              <span className="una-meta-value">
                {product.variants.reduce((sum, v) => sum + v.inventory_quantity, 0)} {t('products.units')}
              </span>
            </div>
            <div className="una-meta-item">
              <span className="una-meta-label">{t('products.variants')}</span>
              <span className="una-meta-value">{product.variants.length}</span>
            </div>
            {isAdminView() && product.country_of_origin && (
              <div className="una-meta-item">
                <span className="una-meta-label"><FaGlobe /> {t('products.countryOfOrigin')}</span>
                <span className="una-detail-value">{product.country_of_origin}</span>
              </div>
            )}
          </div>
          
          {product.body_html && (
            <div className="una-product-description">
              <h3>
                <FaInfoCircle /> {t('products.description')}
              </h3>
              <div className="una-description-content" dangerouslySetInnerHTML={{ __html: product.body_html }} />
            </div>
          )}
        </div>
      </div>
      
      <div className="una-product-grid">
        <div className="una-product-media">
          {Array.isArray(product.tags) && product.tags.length > 0 && (
            <div className="una-product-tags-section">
              <h3>
                <FaTag /> {t('products.tags')}
              </h3>
              <div className="una-product-tags">
                {product.tags.map((tag, index) => (
                  <span key={index} className="una-product-tag">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}

           {/* SEO & Timestamps Section (Admin only) */}
           {isAdminView() && (product.seo_title || product.seo_description || product.created_at || product.updated_at) && (
             <div className="una-product-admin-info-section una-details-card">
               <h3>
                 <FaSearch /> {t('products.seoAndMeta')}
               </h3>
               {product.seo_title && (
                 <div className="una-detail-item">
                   <span className="una-detail-label">{t('products.seoTitle')}</span>
                   <span className="una-detail-value">{product.seo_title}</span>
                 </div>
               )}
               {product.seo_description && (
                 <div className="una-detail-item">
                   <span className="una-detail-label">{t('products.seoDescription')}</span>
                   <span className="una-detail-value">{product.seo_description}</span>
                 </div>
               )}
               {product.created_at && (
                 <div className="una-detail-item">
                   <span className="una-detail-label"><FaCalendarPlus /> {t('products.createdAt')}</span>
                   <span className="una-detail-value">{formatDate(product.created_at)}</span>
                 </div>
               )}
               {product.updated_at && (
                 <div className="una-detail-item">
                   <span className="una-detail-label"><FaCalendarAlt /> {t('products.updatedAt')}</span>
                   <span className="una-detail-value">{formatDate(product.updated_at)}</span>
                 </div>
               )}
             </div>
           )}

        </div>
        
        <div className="una-product-info">
          {product.options && product.options.length > 0 && (
            <div className="una-options-section">
              <h3>
                <FaTags /> {t('products.availableOptions')}
              </h3>
              <div className="una-options-grid">
                {product.options.map(option => (
                  <div key={option.name} className="una-option-group">
                    <h4>{option.name}</h4>
                    <div className="una-option-values">
                      {option.values.map((value, index) => (
                        <span key={index} className="una-option-value">
                          {value}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specifications Section (Admin only) */}
          {isAdminView() && (product.materials?.length > 0 || product.certifications?.length > 0 || product.warranty) && (
            <div className="una-product-specifications-section una-details-card">
              <h3>
                <FaCertificate /> {t('products.specifications')}
              </h3>
              {product.materials?.length > 0 && (
                <div className="una-detail-item">
                  <span className="una-detail-label">{t('products.materials')}</span>
                  <span className="una-detail-value">{product.materials.join(', ')}</span>
                </div>
              )}
              {product.certifications?.length > 0 && (
                <div className="una-detail-item">
                  <span className="una-detail-label">{t('products.certifications')}</span>
                  <span className="una-detail-value">{product.certifications.join(', ')}</span>
                </div>
              )}
              {product.warranty && (
                <div className="una-detail-item">
                  <span className="una-detail-label"><FaShieldAlt /> {t('products.warranty')}</span>
                  <span className="una-detail-value">{product.warranty}</span>
                </div>
              )}
            </div>
          )}

          {/* Logistics Section (Admin only) */}
          {isAdminView() && (product.hs_code || product.package_info || product.minimum_order_quantity || product.lead_time) && (
            <div className="una-product-logistics-section una-details-card">
              <h3>
                <FaShippingFast /> {t('products.logistics')}
              </h3>
              {product.hs_code && (
                <div className="una-detail-item">
                  <span className="una-detail-label"><FaHashtag /> {t('products.hsCode')}</span>
                  <span className="una-detail-value">{product.hs_code}</span>
                </div>
              )}
               {product.minimum_order_quantity && (
                 <div className="una-detail-item">
                   <span className="una-detail-label"><FaSortNumericUpAlt /> {t('products.minOrderQuantity')}</span>
                   <span className="una-detail-value">{product.minimum_order_quantity}</span>
                 </div>
               )}
              {product.lead_time && (
                <div className="una-detail-item">
                  <span className="una-detail-label"><FaCalendarCheck /> {t('products.leadTime')}</span>
                  <span className="una-detail-value">{product.lead_time}</span>
                </div>
              )}
              {product.package_info && (
                <>
                  <h4><FaBox /> {t('products.packageInfo')}</h4>
                  {product.package_info.weight && product.package_info.weight_unit && (
                    <div className="una-detail-item nested">
                      <span className="una-detail-label"><FaWeightHanging /> {t('products.packageWeight')}</span>
                      <span className="una-detail-value">{`${product.package_info.weight} ${product.package_info.weight_unit}`}</span>
                    </div>
                  )}
                  {product.package_info.dimensions && (
                     <div className="una-detail-item nested">
                       <span className="una-detail-label"><FaRulerCombined /> {t('products.packageDimensions')}</span>
                       <span className="una-detail-value">{formatDimensions(product.package_info.dimensions)}</span>
                     </div>
                  )}
                   {product.package_info.items_per_package && (
                     <div className="una-detail-item nested">
                       <span className="una-detail-label"><FaBoxes /> {t('products.itemsPerPackage')}</span>
                       <span className="una-detail-value">{product.package_info.items_per_package}</span>
                     </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="una-variants-section">
            <h3>
              <FaBarcode /> {t('products.variants')}
            </h3>
            <div className="una-variants-grid">
              {product.variants.map(variant => {
                const stockStatus = getStockStatus(variant.inventory_quantity);
                return (
                  <div key={variant.id || variant.sku} className={`una-variant-card ${stockStatus}`}>
                    <div className="una-variant-header">
                      <h4 className="una-variant-title">{variant.title}</h4>
                      <div className={`una-variant-stock ${stockStatus}`}>
                        {stockStatus === 'out-of-stock' && (
                          <><FaExclamationTriangle /> {t('products.outOfStock')}</>
                        )}
                        {stockStatus === 'low-stock' && (
                          <><FaExclamationTriangle /> {variant.inventory_quantity} {t('products.left')}</>
                        )}
                        {stockStatus === 'in-stock' && (
                          <><FaCheck /> {variant.inventory_quantity} {t('products.inStock')}</>
                        )}
                      </div>
                    </div>
                    
                    <div className="una-variant-content">
                      <div className="una-variant-price-group">
                        <div className="una-price-main">
                          <span className="una-price-label">{t('products.price')}</span>
                          <span className="una-price-value">${parseFloat(variant.price).toFixed(2)}</span>
                        </div>
                        
                        {variant.compare_at_price && (
                          <div className="una-price-compare">
                            <span className="una-price-label">{t('products.compareAt')}</span>
                            <span className="una-price-value una-price-compare-value">
                              ${parseFloat(variant.compare_at_price).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="una-variant-details">
                        {variant.sku && (
                          <div className="una-variant-detail-item">
                            <span className="una-detail-label">{t('products.sku')}</span>
                            <span className="una-detail-value">{variant.sku}</span>
                          </div>
                        )}
                        
                        {variant.barcode && (
                          <div className="una-variant-detail-item">
                            <span className="una-detail-label">{t('products.barcode')}</span>
                            <span className="una-detail-value">{variant.barcode}</span>
                          </div>
                        )}
                        
                        <div className="una-variant-detail-item">
                          <span className="una-detail-label">{t('products.taxable')}</span>
                          <span className="una-detail-value">{variant.taxable ? t('common.yes') : t('common.no')}</span>
                        </div>

                        {/* Added Requires Shipping */}
                        <div className="una-variant-detail-item">
                           <span className="una-detail-label"><FaShippingFast /> {t('products.requiresShipping')}</span>
                           <span className="una-detail-value">{variant.requires_shipping ? t('common.yes') : t('common.no')}</span>
                        </div>

                        {/* Add Material if admin view */}
                        {isAdminView() && variant.material && (
                          <div className="una-variant-detail-item">
                            <span className="una-detail-label">{t('products.material')}</span>
                            <span className="una-detail-value">{variant.material}</span>
                          </div>
                        )}

                        {/* Added Inventory Policy if admin view */}
                        {isAdminView() && variant.inventory_policy && (
                          <div className="una-variant-detail-item">
                            <span className="una-detail-label">{t('products.inventoryPolicy')}</span>
                            <span className="una-detail-value">{variant.inventory_policy}</span>
                          </div>
                        )}

                        {/* Added Inventory Management if admin view */}
                        {isAdminView() && variant.inventory_management && (
                          <div className="una-variant-detail-item">
                            <span className="una-detail-label">{t('products.inventoryManagement')}</span>
                            <span className="una-detail-value">{variant.inventory_management}</span>
                          </div>
                        )}

                        {/* Add Dimensions if admin view */}
                        {isAdminView() && variant.dimensions && (
                          <div className="una-variant-detail-item">
                            <span className="una-detail-label"><FaRulerCombined /> {t('products.dimensions')}</span>
                            <span className="una-detail-value">{formatDimensions(variant.dimensions)}</span>
                          </div>
                        )}
                         {/* Add Weight if available */}
                         {variant.weight && variant.weight_unit && (
                           <div className="una-variant-detail-item">
                             <span className="una-detail-label"><FaWeightHanging /> {t('products.weight')}</span>
                             <span className="una-detail-value">{`${variant.weight} ${variant.weight_unit}`}</span>
                           </div>
                         )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;